import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SEVERITY_LEVELS, conditionStatusLabel, conditionStatusColor, conditionTypeLabel, conditionSubtypeLabel, resolvedSubtitle } from '../../utils/trlConstants';
import { REPS_COLORS } from '../../utils/constants';

export const sevColor = (sev) => SEVERITY_LEVELS.find((s) => s.value === sev)?.color || '#9e9e9e';

/** Monospace human ID chip (`26-HOU-0024`); falls back to a short Firestore id until P2-20 backfills. */
export function ConditionIdChip({ condition, size = 'small' }) {
  const id = condition.conditionId || (condition.id ? `#${condition.id.slice(0, 6)}` : '');
  if (!id) return null;
  return <Chip label={id} size={size} variant="outlined" sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 700 }} />;
}

export function StatusChip({ condition, size = 'small' }) {
  const sub = resolvedSubtitle(condition);
  return <Chip label={sub ? `${conditionStatusLabel(condition.status)} · ${sub}` : conditionStatusLabel(condition.status)} size={size} color={conditionStatusColor(condition.status)} sx={{ fontWeight: 700 }} />;
}

/**
 * Severity at every hop (P2-15): Entrant → L1 → L2, plus DREW's suggestion, and a "DREW differs"
 * flag when the suggestion disagrees with the entrant (P2-27). Labels use "Entrant" (Q-26), FLS/DH
 * stay (Tim's own L1/FLS · L2/DH abbreviations).
 */
export function SeverityChips({ condition, size = 'small' }) {
  const ai = condition.aiAnalysis?.suggestedSeverity || null;
  const differs = ai && condition.severityITM && ai !== condition.severityITM;
  const chip = (label, sev, extra = {}) =>
    sev ? <Chip key={label} label={`${label} → Sev ${sev}`} size={size} sx={{ bgcolor: sevColor(sev), color: '#fff', fontWeight: 700, ...extra }} /> : null;
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
      {chip('Entrant', condition.severityITM)}
      {chip('FLS', condition.severityFLS)}
      {chip('DH', condition.severityDH)}
      {ai && (
        <Tooltip title={differs ? 'DREW suggests a different severity than the entrant — L1/L2 pick the final value.' : 'DREW agrees with the entrant.'}>
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            label={`DREW suggests: ${ai}`}
            size={size}
            variant="outlined"
            sx={{ borderColor: sevColor(ai), color: sevColor(ai), fontWeight: 700 }}
          />
        </Tooltip>
      )}
      {differs && <Chip label="DREW differs" size={size} color="warning" variant="outlined" sx={{ fontWeight: 700 }} />}
    </Stack>
  );
}

/** Title line + meta line shared by the queues, the entry list and the dashboard panel. */
export function ConditionHeader({ condition, org, showStatus = true, dense = false }) {
  const nwt = (org?.teams || []).find((t) => t.id === condition.nwtId);
  const ownerDept = (org?.departments || []).find((d) => d.id === condition.ownerDeptId);
  const ownerNwt = (org?.teams || []).find((t) => t.id === condition.ownerNwtId);
  const quads = Array.isArray(condition.repsQuadrants) && condition.repsQuadrants.length ? condition.repsQuadrants : condition.repsQuadrant ? [condition.repsQuadrant] : [];
  const submitted = condition.submittedAt?.toDate ? condition.submittedAt.toDate().toLocaleDateString() : '';
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
        <ConditionIdChip condition={condition} />
        <Typography variant={dense ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.25 }}>
          {condition.title || '(untitled)'}
        </Typography>
        {showStatus && <StatusChip condition={condition} />}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div">
        {nwt?.name || condition.nwtId || '—'} • {conditionTypeLabel(condition.type)}
        {conditionSubtypeLabel(condition) ? ` / ${conditionSubtypeLabel(condition)}` : ''} • REPS:{' '}
        {quads.map((q, i) => (
          <Box key={q} component="span" sx={{ color: REPS_COLORS[q], fontWeight: 700 }}>
            {q.charAt(0).toUpperCase() + q.slice(1)}
            {i < quads.length - 1 ? ', ' : ''}
          </Box>
        ))}
        {' • '}Entrant: <strong>{condition.submittedByName || condition.submittedBy}</strong>
        {submitted ? ` on ${submitted}` : ''}
        {condition.enteredBy && condition.enteredBy !== condition.submittedBy && condition.enteredByName ? ` (entered by ${condition.enteredByName})` : ''}
        {' • '}Owner:{' '}
        <strong>
          {condition.ownerName || ownerNwt?.name || ownerDept?.name || 'not set'}
          {condition.ownerName && ownerDept ? ` (${ownerDept.name})` : ''}
        </strong>
        {condition.priority ? ` • ${condition.priority}` : ''}
        {condition.dueDate ? ` due ${condition.dueDate}` : ''}
      </Typography>
    </Box>
  );
}

export default ConditionHeader;
